import { CheckCircle2, Circle, Clock, Sparkles, AlertCircle } from 'lucide-react'

export interface MerchantJourneyStep {
  key: string
  label: string
  status: 'COMPLETED' | 'CURRENT' | 'UPCOMING' | 'ACTION_REQUIRED'
}

interface MerchantJourneyStepperProps {
  currentStep?: string
  role?: string
}

export function MerchantJourneyStepper({
  currentStep = 'SUBMIT_APPLICATION',
  role = 'CUSTOMER',
}: MerchantJourneyStepperProps) {
  const steps: MerchantJourneyStep[] = [
    { key: 'REGISTER', label: '1. Register', status: 'COMPLETED' },
    { key: 'VERIFY_EMAIL', label: '2. Verify Email', status: 'COMPLETED' },
    {
      key: 'COMPLETE_PROFILE',
      label: '3. Profile',
      status: currentStep === 'COMPLETE_PROFILE' ? 'ACTION_REQUIRED' : 'COMPLETED',
    },
    {
      key: 'SUBMIT_APPLICATION',
      label: '4. Application',
      status:
        currentStep === 'SUBMIT_APPLICATION'
          ? 'ACTION_REQUIRED'
          : ['VERIFY_EMAIL', 'COMPLETE_PROFILE'].includes(currentStep)
          ? 'UPCOMING'
          : 'COMPLETED',
    },
    {
      key: 'PENDING_APPROVAL',
      label: '5. Admin Review',
      status:
        currentStep === 'PENDING_APPROVAL'
          ? 'CURRENT'
          : ['REGISTER', 'VERIFY_EMAIL', 'COMPLETE_PROFILE', 'SUBMIT_APPLICATION'].includes(currentStep)
          ? 'UPCOMING'
          : 'COMPLETED',
    },
    {
      key: 'CHOOSE_SUBSCRIPTION',
      label: '6. Choose Plan',
      status:
        currentStep === 'CHOOSE_SUBSCRIPTION'
          ? 'ACTION_REQUIRED'
          : ['ACTIVE'].includes(currentStep)
          ? 'COMPLETED'
          : 'UPCOMING',
    },
    {
      key: 'ACTIVE',
      label: '7. Ready To Sell',
      status: currentStep === 'ACTIVE' ? 'COMPLETED' : 'UPCOMING',
    },
  ]

  const activeIdx = steps.findIndex((s) => s.status === 'CURRENT' || s.status === 'ACTION_REQUIRED')

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-border/80 bg-white dark:bg-card shadow-sm dark:shadow-elevation-xs p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-border/60 pb-3">
        <span className="font-display text-sm font-bold text-slate-800 dark:text-foreground flex items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          Merchant Onboarding Progress
        </span>
        <span className="text-xs font-semibold text-slate-500 dark:text-muted-foreground bg-slate-100 dark:bg-muted px-2 py-0.5 rounded-full">
          Step {activeIdx + 1} of {steps.length}
        </span>
      </div>

      {/* Steps grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
        {steps.map((step) => {
          let icon: React.ReactNode
          let badgeClass: string

          switch (step.status) {
            case 'COMPLETED':
              icon = <CheckCircle2 className="size-3.5 text-emerald-600 dark:text-emerald-500 shrink-0" />
              badgeClass =
                'border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold'
              break
            case 'ACTION_REQUIRED':
              icon = <AlertCircle className="size-3.5 text-primary animate-pulse shrink-0" />
              badgeClass =
                'border-primary/60 bg-primary/10 dark:bg-primary/10 text-primary font-extrabold ring-1 ring-primary/20 shadow-sm'
              break
            case 'CURRENT':
              icon = <Clock className="size-3.5 text-amber-600 dark:text-amber-500 animate-spin shrink-0" />
              badgeClass =
                'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-500 font-bold'
              break
            default: // UPCOMING
              icon = <Circle className="size-3.5 text-slate-400 dark:text-muted-foreground shrink-0" />
              badgeClass =
                'border-slate-200 dark:border-border/60 bg-slate-50 dark:bg-background/50 text-slate-500 dark:text-muted-foreground'
          }

          return (
            <div
              key={step.key}
              className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl border transition-all ${badgeClass}`}
            >
              {icon}
              <span className="truncate leading-tight">{step.label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
